import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useRetranslateErrors } from '@/lib/forms/useRetranslateErrors';
import { authService } from '@services/auth.service';
import {
  createResetPasswordSchema,
  type ResetPasswordFormValues,
} from '../schemas/resetPassword.schema';

/**
 * Reset-password controller. Reads the token from the URL, validates with zod
 * (score>=3 + match) using `onChange` so the CTA enables live, calls
 * `authService.confirmPasswordReset`, then redirects to /login. Failures are
 * surfaced inline via `error` (toast wiring lands in a separate PR).
 */
export function useResetPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The key, not the resolved text: nothing re-runs the submit on a language toggle.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const schema = useMemo(() => createResetPasswordSchema(t), [t]);
  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
  });

  useRetranslateErrors(form);

  const password = form.watch('password');

  const onSubmit = form.handleSubmit(async (values) => {
    if (!token) {
      setErrorKey('auth.invalidResetToken');
      return;
    }
    setIsSubmitting(true);
    setErrorKey(null);
    try {
      const result = await authService.confirmPasswordReset({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      if (result.success) {
        navigate('/login', { replace: true });
      } else {
        // Localized copy rather than the raw axios message (e.g. "…status code 422").
        setErrorKey('auth.resetLinkExpired');
      }
    } catch {
      setErrorKey('errors.generic');
    } finally {
      setIsSubmitting(false);
    }
  });

  return {
    form,
    onSubmit,
    isSubmitting,
    error: errorKey ? t(errorKey) : null,
    password,
    hasToken: Boolean(token),
    canSubmit: Boolean(token) && form.formState.isValid,
  };
}
